import ApiCallService from "../Service/ApiCallService";

export const NEC_COUNCIL_NAME = "NEC";

export const fetchIsNecCouncilMember = async (councilCode = NEC_COUNCIL_NAME) => {
  try {
    const response = await ApiCallService.get(
      "/committeeMembers/myCouncilMembership?councilCode=" + councilCode,
    );

    return Boolean(
      response?.isNecMember ||
        response?.isMember ||
        response?.data?.isNecMember ||
        response?.data?.isMember,
    );
  } catch (error) {
    console.error("Error checking NEC membership:", error);
    return false;
  }
};

export const extractStringId = (value) => {
  if (!value) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    if (value.$oid) return extractStringId(value.$oid);
    if (value._id) return extractStringId(value._id);
    if (value.id) return extractStringId(value.id);
  }

  return "";
};

export const getLoggedInUserId = (user) =>
  extractStringId(user?.userDetails?._id) ||
  extractStringId(user?._id) ||
  extractStringId(user?.id) ||
  extractStringId(user?.loggedInUserID) ||
  extractStringId(user?.userId) ||
  extractStringId(user);

export const getNECMemberUserId = (member) =>
  extractStringId(member?.user?._id) ||
  extractStringId(member?.userId?._id) ||
  extractStringId(member?.userId) ||
  extractStringId(member?.user);

export const isNECCouncil = (council) => {
  if (typeof council === "string") return council === NEC_COUNCIL_NAME;
  return council?.name === NEC_COUNCIL_NAME || council?.councilName === NEC_COUNCIL_NAME;
};

export const isNECMemberRecord = (member) => isNECCouncil(member?.council);

export const findUserNECMembership = (loggedInUser, necMembers = []) => {
  const loggedInUserId = getLoggedInUserId(loggedInUser);

  if (!loggedInUserId || !Array.isArray(necMembers)) {
    return null;
  }

  return (
    necMembers.find(
      (member) =>
        isNECMemberRecord(member) &&
        getNECMemberUserId(member) === loggedInUserId,
    ) || null
  );
};

const normalizeAccessRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

const getAccessRoleValues = (userDetails = {}) => [
  userDetails?.userRole,
  userDetails?.userRoleLabel,
  userDetails?.role,
  userDetails?.roleLabel,
  userDetails?.userRoleName,
  userDetails?.designation?.policyRole,
  userDetails?.designation?.role,
  userDetails?.designation?.designationKey,
  userDetails?.designationKey,
];

const hasNetworkAccessRole = (userDetails) => {
  return [
    "ADMIN",
    "SUPERADMIN",
    "SUPER_ADMIN",
    "SUPERUSER",
    "SUPER_USER",
    "NEC",
    "NEC_MEMBER",
    "NATIONAL_EXECUTIVE_COUNCIL",
    "NATIONAL_EXECUTIVE_COUNCIL_MEMBER",
    "VIEWER",
  ].some((allowedRole) =>
    getAccessRoleValues(userDetails).some(
      (role) => normalizeAccessRole(role) === allowedRole,
    ),
  );
};

const NEC_POLICY_ROLES = [
  "NEC",
  "NEC MEMBER",
  "NEC_MEMBER",
  "NATIONAL_EXECUTIVE_COUNCIL",
  "NATIONAL EXECUTIVE COUNCIL",
  "NATIONAL_EXECUTIVE_COUNCIL_MEMBER",
  "NATIONAL EXECUTIVE COUNCIL MEMBER",
  "VIEWER",
];

export const canAccessDastakProgramDetails = ({
  userDetails,
  isSuperAdmin = false,
  hasPolicyRole = () => false,
  canByKey = () => false,
} = {}) => {
  if (
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DETAILS") ||
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_VIEW") ||
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_LIST") ||
    canByKey("CORE_NETWORK_VIEW") ||
    hasNetworkAccessRole(userDetails) ||
    isSuperAdmin ||
    hasPolicyRole("VIEWER")
  ) {
    return true;
  }

  return NEC_POLICY_ROLES.some((role) => hasPolicyRole(role));
};

export const canOpenNetworkMemberProfile = ({
  userDetails,
  isSuperAdmin = false,
  hasPolicyRole = () => false,
  canByKey = () => false,
  necMembers = [],
} = {}) => {
  if (
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DETAILS") ||
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_VIEW") ||
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_LIST") ||
    canByKey("CORE_NETWORK_VIEW") ||
    hasNetworkAccessRole(userDetails) ||
    isSuperAdmin ||
    hasPolicyRole("VIEWER")
  ) {
    return true;
  }

  if (NEC_POLICY_ROLES.some((role) => hasPolicyRole(role))) {
    return true;
  }

  return isUserNECMember(userDetails, necMembers);
};

export const isUserNECMember = (loggedInUser, necMembers = []) =>
  Boolean(findUserNECMembership(loggedInUser, necMembers));
